import React from 'react';
import { ToastType } from '@/shared/components/feedback/Toast/Toast.types';
import DashboardMainContent from './DashboardMainContent';
import FeeManagement from '@/features/partner/supplierFee/toHeadquarters/components/FeeManagement';
import DeductionPartnerToHeadPage from '@/features/settlement/deduction/partnerToHead/components/DeductionPartnerToHeadPage';
import DeductionRiderToHeadPage from '@/features/settlement/deduction/riderToHead/components/DeductionRiderToHeadPage';
import DeductionRiderToSupplierPage from '@/features/settlement/deduction/riderToPartner/components/DeductionRiderToSupplierPage';
import PromotionHeadToSupplierPage from '@/features/settlement/promotion/headToPartner/components/PromotionHeadToSupplierPage';
import PromotionHeadToRiderPage from '@/features/settlement/promotion/headToRider/components/PromotionHeadToRiderPage';
import PromotionSupplierToRiderPage from '@/features/settlement/promotion/partnerToRider/components/PromotionSupplierToRiderPage';
import PartnerSettlementHistory from '@/features/settlement/settlementHistory/partnerSettlement/components/PartnerSettlementHistory';
import RiderSettlementHistory from '@/features/settlement/settlementHistory/riderSettlement/components/RiderSettlementHistory';
import RiderStatusPage from '@/features/rider/riderStatus/components/RiderStatusPage';
import RiderDetailInfoPage from '@/features/rider/riderInfoDetail/components/RiderInfoDetailPage';
import AccountingManagement from '@/features/system/withdrawRestriction/components/PartnerAccountingPage';
import VirtualAccountManagement from '@/features/system/virtualAccount/components/VirtualAccountManagement';
import RiderAccountingPage from '@/features/system/withdrawRestriction/components/RiderAccountingPage';
import DeliveryHistoryPage from '@/features/rider/deliveryHistory/components/DeliveryHistoryPage';
import DeliveryHistoryDetailPage from '@/features/rider/deliveryHistoryDetail/components/DeliveryHistoryDetailPage';
import SupplierManagement from '@/features/partner/supplierStatus/components/SupplierManagement';
import PartnerFeeToHeadPage from '@/features/partner/supplierFee/toHeadquarters/components/PartnerFeeToHeadPage';
import RiderFeeToSupplierPage from '@/features/partner/supplierFee/riderToPartner/components/RiderFeeToSupplierPage';
import RiderPreSettlementHistory from '@/features/settlement/settlementHistory/riderPreSettlement/components/RiderPreSettlementHistory';
import HeadWithdrawHistory from '@/features/settlement/settlementHistory/headWithdraw/components/HeadWithdrawHistory';
import TransferHistory from '@/features/system/transfer/components/TransferHistory';
import UserManagementPage from '@/features/system/userManagement/components/UserManagementPage';
import SystemCommonCodeManagementPage from '@/features/system/commonCode/components/SystemCommonCodeManagementPage';
import SystemCommonCodeCreatePage from '@/features/system/commonCode/components/SystemCommonCodeCreatePage';
import UserWithdrawRequestPage from '@/features/system/userWithdraw/components/UserWithdrawRequestPage';
import ReSettlementPage from '@/features/system/reSettlement/components/ReSettlementPage';
import RiderWorkStatusPage from '@/features/settlement/riderWorkStatus/components/RiderWorkStatusPage';
import MaintenanceManagementPage from '@/features/system/maintenance/components/MaintenanceManagementPage';
import NoticeManagementPage from '@/features/notice/components/pages/NoticeManagementPage';
import SettlementUploadPage from '@/features/system/settlementUpload/components/SettlementUploadPage';
import AuthorityManagementPage from '@/features/system/authority/components/AuthorityManagementPage';
import BalanceSnapshotPage from '@/features/system/balanceSnapshot/components/BalanceSnapshotPage';
import SlotManagementPage from '@/features/policy/slotManagement/components/SlotManagementPage';
import HolidayManagementPage from '@/features/policy/holidayManagement/components/HolidayManagementPage';
import StandardAreaManagementPage from '@/features/policy/standardAreaManagement/components/StandardAreaManagementPage';
import { getItem } from '@/shared/utils/storage';

interface DashboardTabRouterProps {
    currentPage: string;
    userType: string;
    selectedRiderId: string | null;
    handlePageChange: (page: string) => void;
    showToast: (msg: string, options: { type: ToastType }) => void;
}

const DashboardTabRouter: React.FC<DashboardTabRouterProps> = ({
                                                                   currentPage,
                                                                   userType,
                                                                   selectedRiderId,
                                                                   handlePageChange,
                                                                   showToast,
                                                               }) => {
    switch (currentPage) {
        case 'dashboard':
            return <DashboardMainContent userType={userType} />;
        case 'delivery_history':
            return <DeliveryHistoryPage />;
        case 'delivery_history_detail':
            return <DeliveryHistoryDetailPage />;
        case 'rider_status':
            return <RiderStatusPage />;
        case 'rider_detail':
            return <RiderDetailInfoPage riderId={selectedRiderId || undefined} />;
        case 'rider_work_status':
            if (userType !== 'HEADQUARTERS') {
                showToast('접근 권한이 없습니다.', { type: ToastType.WARNING });
                handlePageChange('dashboard');
                return <DashboardMainContent userType={userType} />;
            }
            return <RiderWorkStatusPage />;
        case 'partner_status':
            return <SupplierManagement />;
        case 'partner_fee_to_head':
            return <PartnerFeeToHeadPage />;
        case 'rider_fee_to_supplier':
            return <RiderFeeToSupplierPage />;
        case 'partner_settlement':
            return <PartnerSettlementHistory />;
        case 'transaction_history':
            return <HeadWithdrawHistory />;
        case 'virtual_account':
            if (userType !== 'HEADQUARTERS') {
                showToast('접근 권한이 없습니다.', { type: ToastType.WARNING });
                handlePageChange('dashboard');
                return <DashboardMainContent userType={userType} />;
            }
            return <VirtualAccountManagement />;
        case 'deduction_partner_to_head':
            return <DeductionPartnerToHeadPage />;
        case 'deduction_rider_to_head':
            return <DeductionRiderToHeadPage />;
        case 'deduction_rider_to_supplier':
            return <DeductionRiderToSupplierPage />;
        case 'promotion_head_to_supplier':
            return <PromotionHeadToSupplierPage />;
        case 'promotion_head_to_rider':
            return <PromotionHeadToRiderPage />;
        case 'promotion_supplier_to_rider':
            return <PromotionSupplierToRiderPage />;
        case 'fee_management':
            return <FeeManagement />;
        case 'withdrawal_restriction': {
            const currentUserType = getItem<string>('USER_TYPE') || 'HEADQUARTERS';
            return currentUserType === 'SUPPLIER' ? <RiderAccountingPage /> : <AccountingManagement />;
        }
        case 'transfer_management':
            if (userType !== 'HEADQUARTERS') {
                showToast('접근 권한이 없습니다.', { type: ToastType.WARNING });
                handlePageChange('dashboard');
                return <DashboardMainContent userType={userType} />;
            }
            return <TransferHistory />;
        case 'settlement_history':
            return <PartnerSettlementHistory />;
        case 'rider_history_week':
            return <RiderSettlementHistory />;
        case 'rider_history_pre':
            return <RiderPreSettlementHistory />;
        case 'user_management':
            return <UserManagementPage />;
        case 'withdraw_request':
            return <UserWithdrawRequestPage />;
        case 'authority_management':
            return <AuthorityManagementPage />;
        case 'settlement_upload':
            return <SettlementUploadPage />;
        case 'system_settings':
            return <div className="page-placeholder">시스템 설정 페이지</div>;
        case 'backup_restore':
            return <div className="page-placeholder">백업/복구 페이지</div>;
        case 'common_code':
            return <SystemCommonCodeManagementPage />;
        case 'common_code_add':
            return <SystemCommonCodeCreatePage />;
        case 're_settlement':
            return <ReSettlementPage />;
        case 'notice_management':
            return <NoticeManagementPage />;
        case 'maintenance_management': {
            const currentUserType = getItem<string>('USER_TYPE');
            if (currentUserType !== 'HEADQUARTERS') {
                showToast('접근 권한이 없습니다.', { type: ToastType.WARNING });
                handlePageChange('dashboard');
                return <DashboardMainContent userType={userType} />;
            }
            return <MaintenanceManagementPage />;
        }
        case 'balance_snapshot': {
            const currentUserType = getItem<string>('USER_TYPE');
            if (currentUserType !== 'HEADQUARTERS') {
                showToast('접근 권한이 없습니다.', { type: ToastType.WARNING });
                handlePageChange('dashboard');
                return <DashboardMainContent userType={userType} />;
            }
            return <BalanceSnapshotPage />;
        }
        case 'slot_management':
            return <SlotManagementPage />;
        case 'holiday_management':
            return <HolidayManagementPage />;
        case 'standard_area_management':
            return <StandardAreaManagementPage />;
        default:
            return <div className="page-placeholder">페이지를 찾을 수 없습니다</div>;
    }
};

export default DashboardTabRouter;
